import { useState, useEffect } from 'react';
import { apiClient } from '@services/api';
import Icon from '@material-ui/core/Icon';
import CircularProgress from '@material-ui/core/CircularProgress';

// Pódio de ranking de vendas por consultor (mês).
// - Pódio 1: quantidade de vendas (número visível para todos)
// - Pódio 2: valor vendido (R$ só para admin; demais veem só a posição)
// Vendedores "app"/"site" só aparecem para o admin (backend cuida disso).

const MEDALS = ['🥇', '🥈', '🥉'];
const RANK_COLORS = ['#fbbf24', '#cbd5e1', '#d8a06b'];

const fmtBRL = (v) =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const monthLabel = (ym) => {
  const [y, m] = ym.split('-');
  const nomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${nomes[parseInt(m, 10) - 1]}/${y}`;
};

const lastMonths = (n = 24) => {
  const out = [];
  const d = new Date();
  d.setDate(1); // evita pular mês quando hoje é dia 29-31 (setMonth em dia inexistente)
  for (let i = 0; i < n; i++) {
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push(ym);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};

// Bloco visual do pódio (top 3) + lista (4º em diante)
const Podium = ({ title, icon, rows, metric, userId, accent, showTotal }) => {
  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);
  // ordem de exibição do pódio: 2º, 1º, 3º
  const order = [top3[1], top3[0], top3[2]].filter(Boolean);
  const heights = { 1: 92, 2: 70, 3: 56 };

  const valueOf = (r) => {
    if (metric === 'qty') return `${r.qty}`;
    if (metric === 'value') return fmtBRL(r.value);
    return '';
  };

  // Total geral (soma de TODOS os vendedores) — só exibido quando a métrica é visível
  const grandTotal = metric === 'qty'
    ? rows.reduce((acc, r) => acc + (Number(r.qty) || 0), 0)
    : rows.reduce((acc, r) => acc + (Number(r.value) || 0), 0);
  const grandTotalLabel = metric === 'qty' ? `${grandTotal}` : fmtBRL(grandTotal);

  return (
    <div style={{ flex: 1, minWidth: 300, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Icon style={{ color: accent, fontSize: 20 }}>{icon}</Icon>
        <span style={{ color: '#e8f0fb', fontWeight: 700, fontSize: 15 }}>{title}</span>
      </div>

      {rows.length === 0 ? (
        <div style={{ color: '#64748b', textAlign: 'center', padding: '24px 0', fontSize: 13 }}>Sem vendas no período.</div>
      ) : (
        <>
          {/* Pódio top 3 */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
            {order.map((r) => {
              const isMe = userId && Number(r.seller_id) === Number(userId);
              return (
                <div key={r.seller_id} style={{ flex: 1, maxWidth: 130, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, lineHeight: 1 }}>{MEDALS[r.position - 1]}</div>
                  <div style={{
                    fontSize: 12, fontWeight: 700, margin: '4px 0',
                    color: isMe ? accent : '#e8f0fb',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }} title={r.seller_name}>
                    {r.seller_name}{isMe ? ' (você)' : ''}
                  </div>
                  <div style={{
                    height: heights[r.position], borderRadius: '8px 8px 0 0',
                    background: `linear-gradient(180deg, ${RANK_COLORS[r.position - 1]}cc, ${RANK_COLORS[r.position - 1]}44)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: isMe ? `2px solid ${accent}` : '1px solid rgba(255,255,255,0.1)',
                  }}>
                    {/* Fix (2026-08-17): quando o valor é escondido (não-admin em
                        "Valor vendido"), a barra ficava vazia — mostra a posição
                        (1º/2º/3º) no lugar, já que o emoji de medalha some junto com o
                        R$ (correto, só a posição é pública). */}
                    <span style={{ color: '#0f2340', fontWeight: 800, fontSize: 13 }}>
                      {metric ? valueOf(r) : `${r.position}º`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Lista 4º em diante */}
          {rest.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {rest.map((r) => {
                const isMe = userId && Number(r.seller_id) === Number(userId);
                return (
                  <div key={r.seller_id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 10px', borderRadius: 8,
                    background: isMe ? `${accent}22` : 'transparent',
                    border: isMe ? `1px solid ${accent}55` : '1px solid transparent',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <span style={{ color: '#64748b', fontSize: 12, fontWeight: 700, width: 22 }}>{r.position}º</span>
                      <span style={{ color: isMe ? accent : '#cbd5e1', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.seller_name}>
                        {r.seller_name}{isMe ? ' (você)' : ''}
                      </span>
                    </div>
                    {metric && <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>{valueOf(r)}</span>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Total geral (todos os vendedores) — só Administrador */}
          {metric && showTotal && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', marginTop: 12, borderRadius: 8,
              background: `${accent}1a`, border: `1px solid ${accent}40`,
            }}>
              <span style={{ color: '#e8f0fb', fontSize: 13, fontWeight: 700 }}>Total geral</span>
              <span style={{ color: accent, fontSize: 15, fontWeight: 800 }}>{grandTotalLabel}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const dmShort = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}`;
};

const SalesPodium = ({ permission, userId }) => {
  const isAdmin = permission === 'Administrador';
  const [period, setPeriod] = useState('month'); // 'month' | 'week'
  const [month, setMonth] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { period };
    if (isAdmin && period === 'month') params.month = month;
    apiClient.httpInstance.get('/dashboard/podium', { params })
      .then((res) => { setData(res.data.podium); setLoading(false); })
      .catch(() => { setData(null); setLoading(false); });
  }, [period, month, isAdmin]);

  const showValue = !data?.hide_value; // backend só manda value para admin

  const periodLabel = data?.period === 'week'
    ? `Semana (${dmShort(data?.start_date)}–${dmShort(data?.end_date)})`
    : (data?.month ? monthLabel(data.month) : '');

  const toggleBtn = (val, label) => (
    <button
      onClick={() => setPeriod(val)}
      style={{
        padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        border: '1px solid #1e3a5a',
        background: period === val ? '#60a5fa' : 'transparent',
        color: period === val ? '#0f2340' : '#94a3b8',
      }}
    >{label}</button>
  );

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ color: '#e8f0fb', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon style={{ color: '#fbbf24' }}>emoji_events</Icon>
          Pódio {periodLabel ? `— ${periodLabel}` : ''}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {toggleBtn('month', 'Mês')}
          {toggleBtn('week', 'Semana')}
          {isAdmin && period === 'month' && (
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{ background: '#172B4D', color: '#e8f0fb', border: '1px solid #1e3a5a', borderRadius: 8, padding: '6px 10px', fontSize: 13 }}
            >
              {lastMonths(24).map((ym) => <option key={ym} value={ym}>{monthLabel(ym)}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}><CircularProgress size={28} /></div>
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Podium
            title="Quantidade de vendas"
            icon="shopping_cart"
            rows={data?.by_qty || []}
            metric="qty"
            userId={userId}
            accent="#34d399"
            showTotal={isAdmin}
          />
          <Podium
            title="Valor vendido"
            icon="trending_up"
            rows={data?.by_value || []}
            metric={showValue ? 'value' : null}
            userId={userId}
            accent="#60a5fa"
            showTotal={isAdmin}
          />
        </div>
      )}
    </div>
  );
};

export default SalesPodium;
